import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';
import PageHeader from '../../../components/common/PageHeader';

const preferredColumns = [
  'matchedOrganizationName',
  'matchedLevelGroupPath',
  'levelGroupName',
  'name',
  'hostname',
  'operating_system.name',
  'operating_system.version',
  'os.name',
  'serial_number',
  'manufacturer',
  'model',
  'status',
  'last_seen_at',
  'last_check_in',
  'ip_address',
  'public_ip_address',
  'group.name',
  'group_id',
  'matchedLevelGroupId',
];

const hiddenKeys = new Set(['raw']);

function flattenValue(value, prefix = '', output = {}) {
  if (value === null || value === undefined) {
    if (prefix) output[prefix] = '';
    return output;
  }

  if (Array.isArray(value)) {
    output[prefix] = value.map((item) => {
      if (item && typeof item === 'object') return JSON.stringify(item);
      return String(item ?? '');
    }).join(' | ');
    return output;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      if (hiddenKeys.has(key)) return;
      flattenValue(child, prefix ? `${prefix}.${key}` : key, output);
    });
    return output;
  }

  output[prefix] = value;
  return output;
}

function formatHeader(key) {
  const labels = {
    matchedOrganizationName: 'Client',
    matchedLevelGroupPath: 'Dossier Level.io',
    levelGroupName: 'Groupe Level.io',
  };
  if (labels[key]) return labels[key];
  return key
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .join(' / ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatCell(value) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export default function LevelDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`/api/level/devices?includeDeleted=${includeDeleted}`);
      setDevices(data.devices || []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les devices Level.io.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);


  const syncDevices = async () => {
    setSyncing(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post('/api/level/devices/sync');
      setMessage(data.message || 'Synchronisation Level.io terminée.');
      await loadDevices();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Synchronisation Level.io impossible.');
    } finally {
      setSyncing(false);
    }
  };

  const rows = useMemo(() => devices.map((device, index) => ({
    _rowId: device.id || device.device_id || index,
    ...flattenValue(device),
  })), [devices]);

  const columns = useMemo(() => {
    const allKeys = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).filter((key) => key !== '_rowId');
    const orderedKeys = [
      ...preferredColumns.filter((key) => allKeys.includes(key)),
      ...allKeys.filter((key) => !preferredColumns.includes(key)).sort((a, b) => a.localeCompare(b)),
    ];

    return orderedKeys.map((key) => ({
      accessorKey: key,
      header: formatHeader(key),
      minSize: 130,
      size: key.includes('matchedOrganization') ? 220 : 170,
      Cell: ({ cell }) => {
        const value = formatCell(cell.getValue());
        if ((key === 'matchedOrganization.name' || key === 'matchedOrganizationName') && value) {
          return <Chip size="small" color="primary" variant="outlined" label={value} />;
        }
        return (
          <Box
            component="span"
            sx={{
              display: 'block',
              maxWidth: 360,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={value}
          >
            {value}
          </Box>
        );
      },
    }));
  }, [rows]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableGrouping: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    state: { isLoading: loading },
    getRowId: (row) => String(row._rowId),
  });

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <PageHeader section="Infrastructure" title="Level.io Devices" sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={<Switch checked={includeDeleted} onChange={(event) => setIncludeDeleted(event.target.checked)} />}
              label="Inclure supprimés"
            />
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadDevices} disabled={loading || syncing}>
              Actualiser
            </Button>
            <Button variant="contained" onClick={syncDevices} disabled={loading || syncing}>
              {syncing ? 'Synchronisation...' : 'Synchroniser'}
            </Button>
          </Stack>
        </Stack>

        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip label={`${devices.length} device(s)`} />
              <Chip label={`${columns.length} champ(s)`} variant="outlined" />
            </Stack>
            {loading && !rows.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <MaterialReactTable table={table} />
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
